import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { ImageIcon, AlignLeft, AlignRight, X, Lightbulb, Sparkles, Video, ArrowRight } from 'lucide-react';
import { pb } from '@/lib/pocketbase';
import { toast } from 'sonner';
import { useNavigate, useLocation } from 'react-router-dom';
import { detectTextDirection } from '@/utils/textDirection';
import { getProfilePictureUrl } from '@/utils/profileUtils';

interface PostInputSectionProps {
  showBackground?: boolean;
}

export const PostInputSection = ({ showBackground = true }: PostInputSectionProps) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [postContent, setPostContent] = useState('');
  const [selectedImages, setSelectedImages] = useState<File[]>([]);
  const [selectedVideo, setSelectedVideo] = useState<File | null>(null);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  const [videoPreviewUrl, setVideoPreviewUrl] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState('');
  const [textDirection, setTextDirection] = useState<'ltr' | 'rtl'>('ltr');
  const MAX_IMAGES = 10;

  // Load avatar URL when component mounts or user changes
  useEffect(() => {
    const loadAvatar = async () => {
      const url = await getAvatarUrl();
      setAvatarUrl(url);
    };
    loadAvatar();
  }, [user]);

  // Cleanup video preview URL on unmount
  useEffect(() => {
    return () => {
      if (videoPreviewUrl) {
        URL.revokeObjectURL(videoPreviewUrl);
      }
    };
  }, [videoPreviewUrl]);

  const handleInputClick = () => {
    if (!user) {
      // Guest modal
      setIsModalOpen(true);
    } else if (user.role === 'tutor') {
      // Check if tutor has profile picture
      checkTutorProfilePicture();
    } else {
      // Student modal
      setIsModalOpen(true);
    }
  };

  const checkTutorProfilePicture = async () => {
    try {
      const tutorProfile = await pb.collection('tutors').getFirstListItem(`user = "${user?.id}"`);
      
      if (!getProfilePictureUrl(tutorProfile)) {
        toast.error('Complete your profile first', {
          description: 'You need to complete your profile before creating posts.',
        });
        navigate('/profile');
        return;
      }
      
      // Profile picture exists, open modal
      setIsModalOpen(true);
    } catch (error) {
      console.error('Error checking tutor profile:', error);
      toast.error('Failed to verify profile. Please try again.');
    }
  };

  const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;
    
    // Filter valid image files
    const validImages = files.filter(file => file.type.startsWith('image/'));
    if (validImages.length === 0) {
      toast.error('Please select valid image files');
      return;
    }
    
    // Check total count
    const totalCount = selectedImages.length + validImages.length;
    if (totalCount > MAX_IMAGES) {
      toast.error(`Maximum ${MAX_IMAGES} images allowed`);
      return;
    }
    
    // Clear video if images are selected
    if (selectedVideo) {
      setSelectedVideo(null);
      setVideoPreviewUrl('');
    }
    
    // Create preview URLs for new images
    const newPreviewUrls: string[] = [];
    validImages.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        newPreviewUrls.push(result);
        if (newPreviewUrls.length === validImages.length) {
          setPreviewUrls(prev => [...prev, ...newPreviewUrls]);
          setSelectedImages(prev => [...prev, ...validImages]);
          toast.success(`${validImages.length} image${validImages.length > 1 ? 's' : ''} added`);
        }
      };
      reader.readAsDataURL(file);
    });
    
    // Reset the input
    event.target.value = '';
  };

  const removeImage = (index: number) => {
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
    setPreviewUrls(prev => prev.filter((_, i) => i !== index));
  };

  const handleVideoSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('video/')) {
        toast.error('Please select a valid video file');
        return;
      }

      // Check file size (max 500MB - increased from 100MB)
      if (file.size > 500 * 1024 * 1024) {
        toast.error('Video file must be less than 500MB');
        return;
      }
      
      // Clear images if video is selected
      if (selectedImages.length > 0) {
        setSelectedImages([]);
        setPreviewUrls([]);
      }
      
      // Revoke old URL if exists
      if (videoPreviewUrl) {
        URL.revokeObjectURL(videoPreviewUrl);
      }
      
      // Create object URL for video preview
      const objectUrl = URL.createObjectURL(file);
      setVideoPreviewUrl(objectUrl);
      setSelectedVideo(file);
      toast.success('Video loaded for preview');
    }
  };


  const handleSubmitPost = async () => {
    // Allow post with just media (image or video) without requiring text
    if (!postContent.trim() && selectedImages.length === 0 && !selectedVideo) {
      toast.error('Please add some content, an image, or a video');
      return;
    }

    if (!user) {
      toast.error('You must be signed in to create a post');
      return;
    }

    setIsSubmitting(true);
    try {
      // Get tutor profile
      console.log('Fetching tutor profile for user:', user.id);
      const tutorProfile = await pb.collection('tutors').getFirstListItem(`user = "${user?.id}"`);
      console.log('Tutor profile found:', tutorProfile.id);

      // Double-check profile picture exists
      if (!getProfilePictureUrl(tutorProfile)) {
        toast.error('Complete your profile first', {
          description: 'You need to complete your profile before creating posts.',
        });
        setIsModalOpen(false);
        navigate('/profile');
        return;
      }

      // Get the highest display_order to ensure new post appears first
      const existingPosts = await pb.collection('inspire_posts').getList(1, 1, {
        sort: '-display_order',
      });
      const maxOrder = existingPosts.items.length > 0 && existingPosts.items[0].display_order 
        ? existingPosts.items[0].display_order 
        : 0;

      const formData = new FormData();
      formData.append('tutor_id', tutorProfile.id);
      formData.append('content', postContent.trim());
      formData.append('likes_count', '0');
      formData.append('comments_count', '0');
      formData.append('is_published', 'true');
      formData.append('display_order', String(maxOrder + 1));

      // Append all selected images to the 'images' field
      if (selectedImages.length > 0) {
        selectedImages.forEach((image) => {
          formData.append('images', image);
        });
        // Also set first image as 'image' for backward compatibility
        formData.append('image', selectedImages[0]);
      }

      if (selectedVideo) {
        formData.append('video', selectedVideo);
      }

      console.log('Creating inspire post...');
      const createdPost = await pb.collection('inspire_posts').create(formData);
      console.log('Post created successfully:', createdPost.id);
      
      toast.success('Post created successfully!');
      setIsModalOpen(false);
      setPostContent('');
      setSelectedImages([]);
      setSelectedVideo(null);
      setPreviewUrls([]);
      setVideoPreviewUrl('');
    } catch (error: any) {
      console.error('Error creating post:', error);
      
      // Show detailed error message
      let errorMessage = 'Failed to create post. ';
      if (error?.response?.message) {
        errorMessage += error.response.message;
      } else if (error?.message) {
        errorMessage += error.message;
      } else {
        errorMessage += 'Please check your connection and try again.';
      }
      
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getTutorName = () => {
    return user?.name || 'User';
  };

  const getAvatarUrl = async () => {
    if (!user?.id) return '';
    
    try {
      // Try to get tutor profile
      const tutorProfile = await pb.collection('tutors').getFirstListItem(`user = "${user.id}"`);
      if (tutorProfile.profilePictureUrl) {
        return tutorProfile.profilePictureUrl;
      }
      // If tutor has uploaded photo
      if (tutorProfile.profilePicture) {
        return pb.files.getURL(tutorProfile, tutorProfile.profilePicture);
      }
    } catch (e) {
      // Not a tutor or no profile
    }
    
    return user?.avatar || '';
  };

  const renderModalContent = () => {
    if (!user) {
      // Guest modal
      return (
        <div className="space-y-4">
          <p className="text-muted-foreground">
            Join our community to create and interact with inspiring posts from tutors.
          </p>
          <div className="space-y-3">
            <Button 
              onClick={() => {
                setIsModalOpen(false);
                navigate('/auth?mode=register&role=tutor');
              }}
              size="lg"
              className="w-full h-12 text-base"
            >
              Join us as a tutor to create posts
            </Button>
            <Button 
              variant="outline"
              size="lg"
              onClick={() => {
                setIsModalOpen(false);
                navigate('/auth?mode=login');
              }}
              className="w-full h-12 text-base"
            >
              Sign in as a student to interact with tutors
            </Button>
          </div>
        </div>
      );
    } else if (user.role === 'student') {
      // Student modal
      return (
        <div className="space-y-4">
          <p className="text-muted-foreground text-sm leading-relaxed">
            This section is only for tutors. But you can view and interact with tutor posts.
          </p>
          <Button 
            onClick={() => {
              setIsModalOpen(false);
              navigate('/inspire-posts?fromHome=true');
            }}
            size="lg"
            className="w-full h-12 text-base bg-[hsl(var(--brand-violet))] hover:bg-[hsl(var(--brand-violet))]/90 text-white"
          >
            <Sparkles className="h-5 w-5 mr-2" />
            View Inspiring Posts
          </Button>
        </div>
      );
    } else {
      // Tutor modal for creating post
      return (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <Avatar className="h-10 w-10">
              <AvatarImage src={avatarUrl} />
              <AvatarFallback>{getTutorName().charAt(0)}</AvatarFallback>
            </Avatar>
            <div>
              <p className="font-medium">{getTutorName()}</p>
              <p className="text-sm text-muted-foreground">Tutor</p>
            </div>
          </div>
          
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant={textDirection === 'ltr' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setTextDirection('ltr')}
                className="h-8"
              >
                <AlignLeft className="h-4 w-4 mr-1" />
                LTR
              </Button>
              <Button
                type="button"
                variant={textDirection === 'rtl' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setTextDirection('rtl')}
                className="h-8"
              >
                <AlignRight className="h-4 w-4 mr-1" />
                RTL
              </Button>
              <span className="text-xs text-muted-foreground ml-2">
                Auto-detected: {detectTextDirection(postContent).toUpperCase()}
              </span>
            </div>
            
            <Textarea
              placeholder="What knowledge would you like to share today?"
              value={postContent}
              onChange={(e) => {
                const newContent = e.target.value;
                setPostContent(newContent);
                // Auto-detect direction when typing
                const detected = detectTextDirection(newContent);
                setTextDirection(detected);
              }}
              className="min-h-[200px] sm:min-h-[120px] resize-none text-base touch-manipulation"
              dir={textDirection}
              style={{ 
                textAlign: textDirection === 'rtl' ? 'right' : 'left',
                fontSize: '16px' // Prevents zoom on iOS
              }}
            />
          </div>

          {previewUrls.length > 0 && selectedImages.length > 0 && (
            <div className="w-full space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium">Selected Images ({selectedImages.length}/{MAX_IMAGES}):</p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setSelectedImages([]);
                    setPreviewUrls([]);
                  }}
                  className="text-destructive hover:text-destructive h-8"
                  type="button"
                >
                  Clear All
                </Button>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {previewUrls.map((url, index) => (
                  <div key={index} className="relative aspect-square rounded-lg overflow-hidden bg-muted/5 border border-primary/20">
                    <img 
                      src={url} 
                      alt={`Preview ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                    <Button
                      variant="destructive"
                      size="icon"
                      onClick={() => removeImage(index)}
                      className="absolute top-1 right-1 h-6 w-6 rounded-full"
                      type="button"
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
                {selectedImages.length < MAX_IMAGES && (
                  <label 
                    htmlFor="image-upload-grid" 
                    className="aspect-square rounded-lg border-2 border-dashed border-muted-foreground/30 flex items-center justify-center cursor-pointer hover:border-primary/50 hover:bg-muted/20 transition-colors"
                  >
                    <div className="flex flex-col items-center text-muted-foreground">
                      <ImageIcon className="h-6 w-6" />
                      <span className="text-xs mt-1">Add</span>
                    </div>
                    <input
                      type="file"
                      id="image-upload-grid"
                      accept="image/*"
                      multiple
                      onChange={handleImageSelect}
                      className="hidden"
                    />
                  </label>
                )}
              </div>
            </div>
          )}

          {videoPreviewUrl && selectedVideo && (
            <div className="w-full space-y-2">
              <p className="text-sm font-medium">Selected Video:</p>
              <div className="relative border-2 border-primary/20 rounded-lg overflow-hidden bg-black">
                <video 
                  src={videoPreviewUrl}
                  className="w-full max-h-[300px] sm:max-h-[256px] object-contain"
                  controls
                  playsInline
                  preload="metadata"
                />
                <Button
                  variant="destructive"
                  size="lg"
                  onClick={() => {
                    setSelectedVideo(null);
                    setVideoPreviewUrl('');
                  }}
                  className="absolute top-2 right-2 z-10 h-10 w-10 sm:h-9 sm:w-auto sm:px-3"
                  type="button"
                >
                  <Video className="h-5 w-5 sm:mr-1" />
                  <span className="hidden sm:inline">Remove</span>
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">{selectedVideo.name}</p>
            </div>
          )}

          <div className="space-y-3">
            <div className="flex flex-col items-stretch gap-3 sm:gap-2">
              <div className="w-full sm:flex-1 flex flex-col sm:flex-row gap-2">
                <input
                  type="file"
                  id="image-upload"
                  accept="image/*"
                  multiple
                  onChange={handleImageSelect}
                  className="hidden"
                />
                <label htmlFor="image-upload" className="block flex-1">
                  <Button variant="outline" size="lg" className="w-full h-12 sm:h-10" asChild disabled={selectedImages.length >= MAX_IMAGES}>
                    <span className="cursor-pointer flex items-center justify-center">
                      <ImageIcon className="h-5 w-5 mr-2" />
                      Add Photos {selectedImages.length > 0 ? `(${selectedImages.length})` : ''}
                    </span>
                  </Button>
                </label>
                
                <input
                  type="file"
                  id="video-upload"
                  accept="video/mp4,video/quicktime,video/webm,video/x-msvideo"
                  onChange={handleVideoSelect}
                  className="hidden"
                />
                <label htmlFor="video-upload" className="block flex-1">
                  <Button variant="outline" size="lg" className="w-full h-12 sm:h-10" asChild>
                    <span className="cursor-pointer flex items-center justify-center">
                      <Video className="h-2 w-5 mr-2" />
                      Add Video
                    </span>
                  </Button>
                </label>
              </div>
              
              <Button 
                onClick={handleSubmitPost}
                disabled={isSubmitting || (!postContent.trim() && selectedImages.length === 0 && !selectedVideo)}
                size="lg"
                className="w-full sm:w-auto sm:min-w-24 h-12 sm:h-10 sm:flex-shrink-0"
              >
                {isSubmitting ? 'Posting...' : 'Post'}
              </Button>
            </div>
            
            <Button
              onClick={() => {
                setIsModalOpen(false);
                navigate('/inspire-posts?fromHome=true');
              }}
              variant="outline"
              size="lg"
              className="w-full h-12 sm:h-10 bg-[hsl(var(--brand-violet))] hover:bg-[hsl(var(--brand-violet))]/90 text-white border-[hsl(var(--brand-violet))] hover:border-[hsl(var(--brand-violet))]/90"
            >
              <Sparkles className="h-5 w-5 mr-2" />
              View All Inspiring Posts
            </Button>
          </div>
        </div>
      );
    }
  };

  return (
    <>
      <div className="w-full">
        <div className="max-w-2xl md:max-w-4xl lg:max-w-6xl mx-auto px-3 md:px-4 py-3 md:py-4">
          {location.pathname === '/inspire-posts' || location.pathname === '/profile' ? (
            // Show clickable input trigger on inspire-posts page and profile page
            <div 
              className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all hover:bg-muted/50 ${showBackground ? 'bg-card border shadow-sm' : ''}`}
              onClick={handleInputClick}
            >
              <Avatar className="h-10 w-10 shrink-0">
                <AvatarImage src={avatarUrl} />
                <AvatarFallback>{user?.name?.charAt(0) || '?'}</AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 text-muted-foreground text-sm sm:text-base">
                  <Lightbulb className="h-4 w-4 shrink-0 text-yellow-500" />
                  <span className="truncate">
                    {!user 
                      ? "Join to share inspiring content..." 
                      : user.role === 'student' 
                        ? "View inspiring posts from tutors..." 
                        : "What knowledge would you like to share today?"}
                  </span>
                </div>
              </div>
              {user?.role === 'tutor' && (
                <Button size="sm" variant="default" className="shrink-0">
                  Post
                </Button>
              )}
            </div>
          ) : (
            <div className="text-center">
              <Button 
                variant="link"
                size="default"
                onClick={() => navigate('/inspire-posts?fromHome=true')}
                className="text-sm md:text-base font-semibold text-purple-600 hover:underline transition-opacity group"
              >
                Check tutors posts
                <ArrowRight className="inline-block ml-1 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Button>
            </div>
          )}
        </div>
      </div>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent 
          className="fixed inset-0 sm:inset-auto sm:left-[50%] sm:top-[50%] sm:translate-x-[-50%] sm:translate-y-[-50%] translate-x-0 translate-y-0 w-full h-full sm:w-auto sm:h-auto sm:max-h-[90vh] sm:max-w-md border-0 sm:border rounded-none sm:rounded-lg p-0 m-0 flex flex-col max-w-none !duration-500 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-98 data-[state=open]:zoom-in-98"
          onPointerDownOutside={(e) => {
            // Prevent closing when scrolling on touch devices
            if (e.target instanceof HTMLElement && e.target.closest('[data-radix-scroll-area-viewport]')) {
              e.preventDefault();
            }
          }}
        >
          <Button
            variant="ghost"
            size="icon"
            onClick={(e) => {
              e.stopPropagation();
              setIsModalOpen(false);
            }}
            onTouchStart={(e) => {
              e.stopPropagation();
            }}
            className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 z-10 h-8 w-8"
          >
            <X className="h-5 w-5" />
            <span className="sr-only">Close</span>
          </Button>
          <DialogHeader className="px-4 pt-4 sm:p-6 border-b sm:border-0 pb-3 flex-shrink-0">
            <DialogTitle className="text-lg sm:text-xl">
              {!user ? 'Join Our Community' : 
               user.role === 'student' ? 'Tutor Posts Only' : 
               'Create Inspiring Post'}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 min-h-0 overflow-y-auto px-4 sm:px-6 pt-4 pb-6 sm:pt-2 sm:pb-6">
            {renderModalContent()}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};